export type JsonLogicPrimitive = string | number | boolean | null;

export type JsonLogicVariable = {
    var: string;
};

export type JsonLogicFilter = {
    [operator: string]: JsonLogicRule | JsonLogicRule[] | undefined;
};

export type JsonLogicRule = JsonLogicPrimitive | JsonLogicPrimitive[] | JsonLogicVariable | JsonLogicFilter;

export type JsonLogicValue = JsonLogicRule;
export type JsonLogicJoinOperator = 'and' | 'or';
export type FilterDomain = 'audit-log' | 'dataset-query' | 'search' | 'workspace-resource';

export const FILTER_SCHEMA_VERSION = '2026-07-08.1';

export type FilterEnvelope<TLegacy = unknown, TRule = unknown> = {
    advancedFilter?: import('./advancedFilters').AdvancedFilterDefinition<TRule>;
    advancedFilters?: import('./advancedFilters').AdvancedFilterDefinition<TRule>[];
    domain: FilterDomain;
    jsonLogic: JsonLogicRule;
    legacy?: TLegacy;
    metadata?: Record<string, unknown>;
    rules?: TRule[];
    schemaVersion: typeof FILTER_SCHEMA_VERSION | string;
};

const OPERATOR_LABELS: Record<string, string> = {
    '==': 'equals',
    '!=': 'does not equal',
    '>': 'is greater than',
    '>=': 'is greater than or equal to',
    '<': 'is less than',
    '<=': 'is less than or equal to',
    contains: 'contains',
    in: 'is any of',
};

export const isRecord = (value: unknown): value is Record<string, unknown> =>
    Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const JSON_LOGIC_OPERATORS = new Set(['!', '!=', '==', '>', '>=', '<', '<=', 'and', 'contains', 'in', 'or', 'var']);

/**
 * Runtime guard for filter state hydrated from URLs and persisted API payloads.
 * It mirrors the API allowlist and complexity ceilings so malformed legacy data
 * is discarded before it can reach a consumer or be persisted again.
 */
export const isJsonLogicRule = (value: unknown): value is JsonLogicRule => {
    const pending: Array<{ depth: number; value: unknown }> = [{ depth: 0, value }];
    let nodes = 0;

    while (pending.length) {
        const current = pending.pop();
        if (!current || ++nodes > 1_000 || current.depth > 12) {
            return false;
        }
        if (
            current.value === null ||
            typeof current.value === 'boolean' ||
            typeof current.value === 'number' ||
            typeof current.value === 'string'
        ) {
            if (typeof current.value === 'number' && !Number.isFinite(current.value)) {
                return false;
            }
            if (typeof current.value === 'string' && current.value.length > 2_000) {
                return false;
            }
            continue;
        }
        if (Array.isArray(current.value)) {
            if (current.value.length > 100) {
                return false;
            }
            current.value.forEach((child) => pending.push({ depth: current.depth + 1, value: child }));
            continue;
        }
        if (!isRecord(current.value)) {
            return false;
        }

        const entries = Object.entries(current.value);
        if (!entries.length) {
            continue;
        }
        if (entries.length !== 1) {
            return false;
        }

        const [operator, operand] = entries[0];
        if (!JSON_LOGIC_OPERATORS.has(operator)) {
            return false;
        }
        if (operator === 'var') {
            if (typeof operand !== 'string' || !operand.length || operand.length > 250) {
                return false;
            }
            continue;
        }
        if ((operator === 'and' || operator === 'or') && !Array.isArray(operand)) {
            return false;
        }
        if (['!=', '==', '>', '>=', '<', '<=', 'contains', 'in'].includes(operator)) {
            if (!Array.isArray(operand) || operand.length !== 2) {
                return false;
            }
        }
        if (operator === '!' && Array.isArray(operand) && operand.length !== 1) {
            return false;
        }
        pending.push({ depth: current.depth + 1, value: operand });
    }

    return true;
};

export const jsonLogicField = (field: string): JsonLogicVariable => ({ var: field });

export const compactJsonLogicConditions = (
    conditions: Array<JsonLogicRule | undefined>,
    join: JsonLogicJoinOperator = 'and',
): JsonLogicFilter => {
    const activeConditions = conditions.filter((condition): condition is JsonLogicRule => Boolean(condition));

    return activeConditions.length ? ({ [join]: activeConditions } as JsonLogicFilter) : {};
};

export const createFilterEnvelope = <TLegacy = unknown, TRule = unknown>({
    advancedFilter,
    advancedFilters,
    domain,
    jsonLogic,
    legacy,
    metadata,
    rules,
}: Omit<FilterEnvelope<TLegacy, TRule>, 'schemaVersion'>): FilterEnvelope<TLegacy, TRule> => ({
    ...(advancedFilter ? { advancedFilter } : {}),
    ...(advancedFilters?.length ? { advancedFilters } : {}),
    domain,
    jsonLogic,
    legacy,
    metadata,
    rules,
    schemaVersion: FILTER_SCHEMA_VERSION,
});

export const isFilterEnvelope = <TLegacy = unknown, TRule = unknown>(
    value: unknown,
): value is FilterEnvelope<TLegacy, TRule> =>
    isRecord(value) &&
    typeof value.schemaVersion === 'string' &&
    typeof value.domain === 'string' &&
    'jsonLogic' in value;

export const unwrapFilterEnvelope = <TLegacy = unknown>(value: unknown): TLegacy | unknown =>
    isFilterEnvelope<TLegacy>(value) ? (value.legacy ?? {}) : value;

export const getFilterEnvelopeJsonLogic = (value: unknown): JsonLogicRule | undefined =>
    isFilterEnvelope(value) ? value.jsonLogic : undefined;

export const isJsonLogicRuleEmpty = (rule: unknown) => {
    if (!rule) {
        return true;
    }

    if (!isRecord(rule)) {
        return false;
    }

    if (Array.isArray(rule.and)) {
        return rule.and.length === 0;
    }

    if (Array.isArray(rule.or)) {
        return rule.or.length === 0;
    }

    return Object.keys(rule).length === 0;
};

export const resolveJsonLogicValue = (rule: unknown, data: Record<string, unknown>): unknown => {
    if (Array.isArray(rule)) {
        return rule;
    }

    if (!isRecord(rule)) {
        return rule;
    }

    if (typeof rule.var === 'string') {
        return rule.var.split('.').reduce<unknown>((value, key) => (isRecord(value) ? value[key] : undefined), data);
    }

    return evaluateJsonLogicRule(rule, data);
};

const includesValue = (haystack: unknown, needle: unknown) => {
    if (Array.isArray(haystack) && Array.isArray(needle)) {
        return needle.some((item) => haystack.includes(item));
    }

    if (Array.isArray(haystack)) {
        return haystack.includes(needle);
    }

    if (Array.isArray(needle)) {
        return needle.includes(haystack);
    }

    return String(haystack ?? '')
        .toLowerCase()
        .includes(String(needle ?? '').toLowerCase());
};

const compareJsonLogicValues = (left: unknown, right: unknown, operator: '>' | '>=' | '<' | '<=') => {
    const leftValue = typeof left === 'number' ? left : Number(left);
    const rightValue = typeof right === 'number' ? right : Number(right);
    const canCompareAsNumber = Number.isFinite(leftValue) && Number.isFinite(rightValue);
    const comparableLeft = canCompareAsNumber ? leftValue : String(left ?? '');
    const comparableRight = canCompareAsNumber ? rightValue : String(right ?? '');

    if (operator === '>') {
        return comparableLeft > comparableRight;
    }

    if (operator === '>=') {
        return comparableLeft >= comparableRight;
    }

    if (operator === '<') {
        return comparableLeft < comparableRight;
    }

    return comparableLeft <= comparableRight;
};

export const evaluateJsonLogicRule = (rule: unknown, data: Record<string, unknown>): boolean => {
    if (isJsonLogicRuleEmpty(rule)) {
        return true;
    }

    if (!isRecord(rule)) {
        return Boolean(rule);
    }

    if (typeof rule.var === 'string') {
        return Boolean(resolveJsonLogicValue(rule, data));
    }

    if (Array.isArray(rule.and)) {
        return rule.and.every((condition) => evaluateJsonLogicRule(condition, data));
    }

    if (Array.isArray(rule.or)) {
        return rule.or.some((condition) => evaluateJsonLogicRule(condition, data));
    }

    if (Array.isArray(rule['=='])) {
        const [left, right] = rule['=='];

        return resolveJsonLogicValue(left, data) === resolveJsonLogicValue(right, data);
    }

    if (Array.isArray(rule['!='])) {
        const [left, right] = rule['!='];

        return resolveJsonLogicValue(left, data) !== resolveJsonLogicValue(right, data);
    }

    if (Array.isArray(rule['>'])) {
        const [left, right] = rule['>'];

        return compareJsonLogicValues(resolveJsonLogicValue(left, data), resolveJsonLogicValue(right, data), '>');
    }

    if (Array.isArray(rule['>='])) {
        const [left, right] = rule['>='];

        return compareJsonLogicValues(resolveJsonLogicValue(left, data), resolveJsonLogicValue(right, data), '>=');
    }

    if (Array.isArray(rule['<'])) {
        const [left, right] = rule['<'];

        return compareJsonLogicValues(resolveJsonLogicValue(left, data), resolveJsonLogicValue(right, data), '<');
    }

    if (Array.isArray(rule['<='])) {
        const [left, right] = rule['<='];

        return compareJsonLogicValues(resolveJsonLogicValue(left, data), resolveJsonLogicValue(right, data), '<=');
    }

    if (Array.isArray(rule.in)) {
        const [needle, haystack] = rule.in;

        return includesValue(resolveJsonLogicValue(haystack, data), resolveJsonLogicValue(needle, data));
    }

    if (Array.isArray(rule.contains)) {
        const [haystack, needle] = rule.contains;

        return includesValue(resolveJsonLogicValue(haystack, data), resolveJsonLogicValue(needle, data));
    }

    return true;
};

export const humanizeFilterKey = (value: string) =>
    value
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (letter) => letter.toUpperCase());

export const formatJsonLogicValue = (value: unknown): string => {
    if (Array.isArray(value)) {
        return value.map(formatJsonLogicValue).join(', ');
    }

    if (isRecord(value)) {
        if (typeof value.var === 'string') {
            return humanizeFilterKey(value.var);
        }

        if (typeof value.label === 'string') {
            return value.label;
        }

        if (typeof value.name === 'string') {
            return value.name;
        }

        if (typeof value.symbol === 'string') {
            return value.symbol;
        }

        return JSON.stringify(value);
    }

    if (value === null || value === undefined) {
        return 'Empty';
    }

    return String(value);
};

export const summarizeJsonLogicFilter = (filters: unknown): string[] => {
    const filterExpression = getFilterEnvelopeJsonLogic(filters) ?? filters;

    if (!isRecord(filterExpression)) {
        return [];
    }

    if (Array.isArray(filterExpression.rules)) {
        return filterExpression.rules
            .map((rule) => {
                if (!isRecord(rule)) {
                    return null;
                }

                const field = typeof rule.field === 'string' ? humanizeFilterKey(rule.field) : 'Field';
                const operator = typeof rule.operator === 'string' ? humanizeFilterKey(rule.operator) : 'matches';
                const value = Array.isArray(rule.values) ? rule.values : [rule.value];

                return `${field} ${operator.toLowerCase()} ${formatJsonLogicValue(value)}`;
            })
            .filter(Boolean) as string[];
    }

    return Object.entries(filterExpression).flatMap(([operator, operands]) => {
        if ((operator === 'and' || operator === 'or') && Array.isArray(operands)) {
            return operands.flatMap(summarizeJsonLogicFilter);
        }

        if (operator in OPERATOR_LABELS && Array.isArray(operands)) {
            const [left, right] = operands;
            const field =
                isRecord(left) && typeof left.var === 'string'
                    ? humanizeFilterKey(left.var)
                    : formatJsonLogicValue(left);

            return [`${field} ${OPERATOR_LABELS[operator]} ${formatJsonLogicValue(right)}`];
        }

        return [`${humanizeFilterKey(operator)} ${formatJsonLogicValue(operands)}`];
    });
};
